import { useState, useCallback, useMemo, useEffect } from 'react'
import { checkPermission } from '@app/common/utils/helpers'

/**
 * Hook for managing template suggestions functionality
 * @param {Object} options - Configuration options
 * @param {Array} options.templates - Array of template objects
 * @param {string} options.audienceFilter - Filter templates by audience ('everyone', 'internal', or null)
 * @param {boolean} options.showPreApprovedOnly - Only show pre-approved templates
 * @param {number} options.minCharacters - Minimum characters before showing suggestions
 * @param {number} options.maxSuggestions - Maximum number of suggestions to show
 * @returns {Object} Hook state and methods
 */
export const useTemplateSuggestions = ({
  templates = [],
  audienceFilter = null,
  showPreApprovedOnly = false,
  minCharacters = 3,
  maxVisibleSuggestions = 3,
  inputValue = '', // Accept inputValue from props
  onTemplateSelect,
} = {}) => {
  const [showSuggestions, setShowSuggestions] = useState(false)
  const userCanApproveMessage = checkPermission('approve_conversation_messages')

  // Update showSuggestions when inputValue changes
  useEffect(() => {
    setShowSuggestions(inputValue.length >= minCharacters)
  }, [inputValue, minCharacters])

  // Filter templates based on audience and pre-approval status
  const filteredTemplates = useMemo(() => {
    let filtered = templates

    // Filter out "vendor" audience templates for users without approval permission
    if (!userCanApproveMessage) {
      filtered = filtered.filter(template => template.audience !== 'vendor')
    }

    // Filter by audience if specified
    if (audienceFilter) {
      filtered = filtered.filter(template => template.audience === audienceFilter)
    }

    // Filter by pre-approved status if specified
    if (showPreApprovedOnly) {
      filtered = filtered.filter(template => template.is_pre_approved === true)
    }

    return filtered
  }, [templates, audienceFilter, showPreApprovedOnly, userCanApproveMessage])

  // Simple text-based search (fallback if Fuse.js is not available)
  const searchTemplates = useMemo(() => {
    if (!inputValue.trim() || inputValue.length < minCharacters) {
      return []
    }

    const searchTerm = inputValue.toLowerCase()
    const results = filteredTemplates.filter(template => {
      const nameMatch = template.name?.toLowerCase().includes(searchTerm)
      const textMatch = template.text?.toLowerCase().includes(searchTerm)
      return nameMatch || textMatch
    })

    return results
  }, [inputValue, filteredTemplates, minCharacters])

  // Handle template selection
  const handleTemplateSelect = (template) => {
    if (onTemplateSelect) {
      onTemplateSelect(template)
    }
    setShowSuggestions(false)
  }

  // Handle view all templates
  const handleViewAllTemplates = useCallback((onViewAllTemplates) => {
    if (onViewAllTemplates) {
      onViewAllTemplates()
    }
    setShowSuggestions(false)
  }, [])

  // Reset suggestions
  const resetSuggestions = useCallback(() => {
    setShowSuggestions(false)
  }, [])

  return {
    // State
    showSuggestions,
    suggestedTemplates: searchTemplates,

    // Methods
    handleTemplateSelect,
    handleViewAllTemplates,
    resetSuggestions,
    setShowSuggestions,
  }
}

export default useTemplateSuggestions
